import { Link } from 'react-router-dom';
import styles from './Legal.module.css';
import {
  LEGAL_LAST_UPDATED,
  TODO_ENTITY,
  TODO_JURISDICTION,
  TODO_CONTACT_EMAIL,
} from './legalMeta';

// Privacy Policy.
//
// Written as if user accounts exist (per maintainer directive — login is a
// planned near-term feature; see legalMeta.js ACCOUNTS_ASSUMED). Account and
// authentication handling is therefore described in the present tense. To
// avoid unsupported factual claims, the SPECIFICS the codebase cannot yet
// substantiate — the identity/auth provider, any payment processor, and any
// analytics vendor — are marked as placeholders or described conditionally.
//
// Accurate as of this build:
//   - The frontend holds NO third-party API keys; all market data is fetched
//     server-side by the backend (funviz-amplify). See README.
//   - The only client-side storage is functional localStorage for UI
//     preferences (which analysis cards are expanded). There is NO analytics,
//     NO advertising/tracking pixels, and NO tracking cookies in the frontend
//     — hence no cookie-consent banner (only strictly-functional storage is
//     used). This is described truthfully below.
const PrivacyPolicy = () => (
  <main className={styles.page}>
    <article className={styles.content}>
      <header className={styles.header}>
        <h1 className={styles.title}>Privacy Policy</h1>
        <p className={styles.updated}>Last updated: {LEGAL_LAST_UPDATED}</p>
      </header>

      <section className={styles.section}>
        <p>
          This Privacy Policy explains how{' '}
          <span className={styles.placeholder}>{TODO_ENTITY}</span> (&ldquo;FunViz&rdquo;,
          &ldquo;we&rdquo;, &ldquo;us&rdquo;) handles information in connection
          with the FunViz website and application (the &ldquo;Service&rdquo;).
          It is provided for transparency and does not constitute legal advice.
        </p>
      </section>

      <section className={styles.section} aria-labelledby="pp-collect">
        <h2 id="pp-collect" className={styles.sectionHeading}>
          Information we collect
        </h2>
        <ul>
          <li>
            <strong>Account information.</strong> When you create an account, we
            collect the information you provide to register and sign in — such
            as your email address and authentication credentials. Account
            sign-in and identity management are handled through a third-party
            authentication provider (
            <span className={styles.placeholder}>{TODO_ENTITY}</span> to specify
            the provider); we do not store your password in plain text.
          </li>
          <li>
            <strong>Data you enter.</strong> The stock symbols you look up and
            the filters and chart settings you choose are used to operate the
            Service and produce the charts you request.
          </li>
          <li>
            <strong>Device and log information.</strong> Like most websites, our
            hosting and backend infrastructure may automatically record
            technical information such as IP address, browser type, and request
            timestamps for security, reliability, and abuse-prevention purposes.
          </li>
          <li>
            <strong>Local browser storage.</strong> We store a small amount of
            functional data in your browser&rsquo;s local storage to remember UI
            preferences (for example, which analysis cards you have expanded).
            This is strictly functional, stays on your device, and is not used
            to track you.
          </li>
          <li>
            <strong>Payment information (if applicable).</strong> If paid
            subscriptions are offered and you purchase one, payment is processed
            by a third-party payment processor. We do not receive or store your
            full payment card details; we retain only limited subscription
            status information needed to provide the paid features.
          </li>
        </ul>
      </section>

      <section className={styles.section} aria-labelledby="pp-cookies">
        <h2 id="pp-cookies" className={styles.sectionHeading}>
          Cookies and analytics
        </h2>
        <p>
          The FunViz frontend does not use advertising or third-party tracking
          cookies, and does not run third-party analytics or advertising
          scripts. The only client-side storage we use is the strictly
          functional local storage described above. Because we do not use
          non-essential tracking, no cookie-consent banner is presented. If we
          introduce analytics or non-essential cookies in the future, we will
          update this policy and provide any consent controls required in your
          jurisdiction.
        </p>
      </section>

      <section className={styles.section} aria-labelledby="pp-use">
        <h2 id="pp-use" className={styles.sectionHeading}>
          How we use information
        </h2>
        <ul>
          <li>To operate, maintain, and secure the Service.</li>
          <li>To create and manage your account and authenticate you.</li>
          <li>
            To provide the features you request (looking up symbols, generating
            charts, screening stocks).
          </li>
          <li>
            To administer subscriptions and process payments, where paid
            features apply.
          </li>
          <li>
            To detect, prevent, and address technical issues, abuse, and
            security incidents.
          </li>
        </ul>
      </section>

      <section className={styles.section} aria-labelledby="pp-sharing">
        <h2 id="pp-sharing" className={styles.sectionHeading}>
          How information is shared
        </h2>
        <p>
          We share information with service providers who operate the Service on
          our behalf — for example, our cloud hosting provider, our
          authentication provider, and (where applicable) our payment processor
          — only as needed to provide the Service. We may disclose information
          where required by law. We do not sell your personal information.
        </p>
        <p>
          Market and financial data displayed in the Service originate from a
          third-party market-data provider and are retrieved by our backend, not
          by sending your personal information to that provider.
        </p>
      </section>

      <section className={styles.section} aria-labelledby="pp-retention">
        <h2 id="pp-retention" className={styles.sectionHeading}>
          Data retention
        </h2>
        <p>
          We retain account information for as long as your account is active
          and as needed to provide the Service, comply with our legal
          obligations, resolve disputes, and enforce our agreements. Technical
          logs are retained for a limited period for security and reliability.
        </p>
      </section>

      <section className={styles.section} aria-labelledby="pp-rights">
        <h2 id="pp-rights" className={styles.sectionHeading}>
          Your choices and rights
        </h2>
        <p>
          Depending on where you live, you may have rights to access, correct,
          or delete your personal information, or to object to or restrict
          certain processing. You can clear the functional data stored in your
          browser at any time through your browser settings. To make a request
          regarding your account information, contact us at{' '}
          <span className={styles.placeholder}>{TODO_CONTACT_EMAIL}</span>.
        </p>
      </section>

      <section className={styles.section} aria-labelledby="pp-contact">
        <h2 id="pp-contact" className={styles.sectionHeading}>
          Contact
        </h2>
        <p>
          Questions about this policy can be directed to{' '}
          <span className={styles.placeholder}>{TODO_CONTACT_EMAIL}</span>. This
          policy is governed by the laws of{' '}
          <span className={styles.placeholder}>{TODO_JURISDICTION}</span>.
        </p>
      </section>

      <p>
        See also our <Link to="/terms" className={styles.backLink}>Terms of Service</Link>{' '}
        and <Link to="/disclaimer" className={styles.backLink}>Disclaimer</Link>.
      </p>
      <p>
        <Link to="/" className={styles.backLink}>&larr; Back to FunViz</Link>
      </p>
    </article>
  </main>
);

export default PrivacyPolicy;
